import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { partnerDiscountService, programService } from '../services/api';
import { Program, ProgramPricingResponse, ProgramPricingPathway } from '../types';

type PathwaySelectionBuilderProps = {
  initialProgramId?: number;
  initialProgram?: Program | null;
  hideProgramSelect?: boolean;
  embedded?: boolean;
  allowPublic?: boolean;
  hideHeader?: boolean;
  headline?: string;
  subhead?: string;
  autoShowSelection?: boolean;
  selection?: {
    programId: number | null;
    trackIds: number[];
    addonIds: number[];
  };
  initialSelection?: {
    programId?: number | null;
    trackIds?: number[];
    addonIds?: number[];
  };
  onSelectionChange?: (selection: {
    programId: number | null;
    trackIds: number[];
    addonIds: number[];
    totalPrice: number;
    totalDurationDays: number;
  }) => void;
};

const PathwaySelectionBuilder: React.FC<PathwaySelectionBuilderProps> = ({
  initialProgramId,
  initialProgram,
  hideProgramSelect = false,
  embedded = false,
  allowPublic = false,
  hideHeader = false,
  headline,
  subhead,
  autoShowSelection = false,
  selection,
  initialSelection,
  onSelectionChange,
}) => {
  const { user } = useAuth();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [pricing, setPricing] = useState<ProgramPricingResponse | null>(null);
  const [selectedTrackIds, setSelectedTrackIds] = useState<number[]>([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState<number[]>([]);
  const [partnerDiscount, setPartnerDiscount] = useState(0);
  const [partnerDiscountCurrency, setPartnerDiscountCurrency] = useState<string | null>(null);
  const [showSelection, setShowSelection] = useState(false);
  const selectionRef = useRef<HTMLDivElement | null>(null);
  const initialSelectionAppliedRef = useRef(false);
  const isControlled = selection != null;

  const isAdmin = Boolean(user?.is_admin);

  useEffect(() => {
    if (initialProgram) {
      setPrograms((prev) => {
        const next = prev.filter((program) => program.id !== initialProgram.id);
        return [initialProgram, ...next];
      });
      setSelectedProgramId(initialProgram.id);
    }
  }, [initialProgram]);

  useEffect(() => {
    const loadPrograms = async () => {
      const list = isAdmin
        ? await programService.getAllPrograms()
        : await programService.getPrograms();
      setPrograms(list);
      if (list.length > 0) {
        if (!isControlled) {
          if (initialSelection?.programId) {
            setSelectedProgramId(initialSelection.programId);
          } else if (initialProgramId) {
            setSelectedProgramId(initialProgramId);
          } else {
            setSelectedProgramId(list[0].id);
          }
        } else if (!selection?.programId) {
          onSelectionChange?.({
            programId: list[0].id,
            trackIds: selection?.trackIds ?? [],
            addonIds: selection?.addonIds ?? [],
            totalPrice: 0,
            totalDurationDays: 0,
          });
        }
      }
    };
    loadPrograms();
  }, [
    isAdmin,
    initialProgramId,
    initialSelection?.programId,
    isControlled,
    onSelectionChange,
    selection?.programId,
    selection?.trackIds,
    selection?.addonIds,
  ]);

  const effectiveProgramId = selection?.programId ?? selectedProgramId;
  const normalizeIds = (value: unknown): number[] => {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => (typeof item === 'number' ? item : Number(item)))
      .filter((item) => Number.isFinite(item));
  };
  const effectiveTrackIds: number[] = Array.isArray(selection?.trackIds)
    ? normalizeIds(selection!.trackIds)
    : normalizeIds(selectedTrackIds);
  const effectiveAddonIds: number[] = Array.isArray(selection?.addonIds)
    ? normalizeIds(selection!.addonIds)
    : normalizeIds(selectedAddonIds);

  useEffect(() => {
    if (!effectiveProgramId) return;
    initialSelectionAppliedRef.current = false;
    programService.getProgramPricing(effectiveProgramId)
      .then((data) => {
        setPricing(data);
      });
  }, [effectiveProgramId]);

  useEffect(() => {
    if (!pricing || !effectiveProgramId) return;
    if (isControlled) return;
    if (initialSelection && initialSelection.programId === effectiveProgramId && !initialSelectionAppliedRef.current) {
      setSelectedTrackIds(initialSelection.trackIds ?? []);
      setSelectedAddonIds(initialSelection.addonIds ?? []);
      initialSelectionAppliedRef.current = true;
      return;
    }
    if (!initialSelection) {
      setSelectedTrackIds([]);
      setSelectedAddonIds([]);
    }
  }, [pricing, effectiveProgramId, initialSelection, isControlled]);

  useEffect(() => {
    if (!showSelection) return;
    if (!selectionRef.current) return;
    selectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showSelection]);

  useEffect(() => {
    if (autoShowSelection) {
      setShowSelection(true);
    }
  }, [autoShowSelection]);

  useEffect(() => {
    if (!user) return;
    partnerDiscountService.getEligibility()
      .then((summary) => {
        const best = summary.best_discount;
        setPartnerDiscount(best?.discount_value ?? 0);
        setPartnerDiscountCurrency(best?.discount_type === 'percent' ? '%' : (best?.currency ?? null));
      })
      .catch(() => {
        setPartnerDiscount(0);
        setPartnerDiscountCurrency(null);
      });
  }, [user]);

  const selectedProgram = useMemo(() => (
    programs.find((program) => program.id === effectiveProgramId) || null
  ), [programs, effectiveProgramId]);

  const tracks = useMemo(() => (
    pricing?.cohorts ?? pricing?.pathways?.filter((pathway) => (pathway.pathway_type || 'track') === 'track') ?? []
  ), [pricing]);

  const addons = useMemo(() => (
    pricing?.extensions ?? pricing?.pathways?.filter((pathway) => (pathway.pathway_type || 'track') === 'addon') ?? []
  ), [pricing]);

  const estimateDurationDays = useCallback((pathway: ProgramPricingPathway) => {
    if (pathway.duration_days != null) return pathway.duration_days;
    if (pathway.start_date && pathway.end_date) {
      const start = new Date(pathway.start_date);
      const end = new Date(pathway.end_date);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        return diff >= 0 ? diff + 1 : null;
      }
    }
    return null;
  }, []);

  const computeSelectionDurationDays = useCallback((items: ProgramPricingPathway[]) => {
    let earliest: number | null = null;
    let latest: number | null = null;
    for (const item of items) {
      if (item.start_date) {
        const start = new Date(item.start_date).getTime();
        if (!Number.isNaN(start)) {
          earliest = earliest == null ? start : Math.min(earliest, start);
        }
      }
      if (item.end_date) {
        const end = new Date(item.end_date).getTime();
        if (!Number.isNaN(end)) {
          latest = latest == null ? end : Math.max(latest, end);
        }
      }
    }
    if (earliest != null && latest != null && latest >= earliest) {
      return Math.floor((latest - earliest) / (1000 * 60 * 60 * 24)) + 1;
    }
    return items
      .map((pkg) => estimateDurationDays(pkg) ?? 0)
      .reduce((sum, days) => sum + days, 0);
  }, [estimateDurationDays]);

  const estimatePrice = (pathway: ProgramPricingPathway) => (
    pathway.selling_price ?? pathway.estimated_selling_price ?? 0
  );

  const parseDate = useCallback((value?: string | null) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const diffDays = useCallback((start: Date, end: Date) => {
    const ms = start.getTime() - end.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  }, []);

  const isAddonCompatible = useCallback((addon: ProgramPricingPathway, selected: ProgramPricingPathway[]) => {
    if (selected.length === 0) return false;
    const addonStart = parseDate(addon.start_date);
    const addonEnd = parseDate(addon.end_date);
    if (!addonStart && !addonEnd) return true;
    const datedTracks = selected.filter((track) => parseDate(track.start_date) || parseDate(track.end_date));
    if (datedTracks.length === 0) return true;
    const allowedGapDays = 5;
    return datedTracks.some((track) => {
      const trackStart = parseDate(track.start_date);
      const trackEnd = parseDate(track.end_date);
      if (trackEnd && addonStart) {
        const gap = diffDays(addonStart, trackEnd);
        if (gap >= 0 && gap <= allowedGapDays) return true;
      }
      if (trackStart && addonEnd) {
        const gap = diffDays(trackStart, addonEnd);
        if (gap >= 0 && gap <= allowedGapDays) return true;
      }
      return false;
    });
  }, [diffDays, parseDate]);

  const formatDuration = (pathway: ProgramPricingPathway) => {
    const days = estimateDurationDays(pathway);
    if (!days) return '—';
    const weeks = Math.round((days / 7) * 10) / 10;
    if (weeks >= 1) {
      const weekLabel = weeks === 1 ? 'week' : 'weeks';
      return `${weeks} ${weekLabel}`;
    }
    return `${days} days`;
  };

  const currency = 'USD';
  const formatCurrency = (value: number) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);

  const selectedTracks = useMemo(() => (
    tracks.filter((pkg) => pkg.id && effectiveTrackIds.includes(pkg.id))
  ), [tracks, effectiveTrackIds]);

  const selectedAddons = useMemo(() => (
    addons.filter((pkg) => pkg.id && effectiveAddonIds.includes(pkg.id))
  ), [addons, effectiveAddonIds]);

  const totalDurationDays = useMemo(() => (
    computeSelectionDurationDays([...selectedTracks, ...selectedAddons])
  ), [selectedTracks, selectedAddons, computeSelectionDurationDays]);

  const totalDurationSummary = useMemo(() => {
    if (!totalDurationDays) return '—';
    const weeks = Math.floor(totalDurationDays / 7);
    const extraDays = totalDurationDays % 7;
    if (weeks >= 1 && extraDays) {
      const weekLabel = weeks === 1 ? 'week' : 'weeks';
      return `${weeks} ${weekLabel} + ${extraDays} days`;
    }
    if (weeks >= 1) {
      const weekLabel = weeks === 1 ? 'week' : 'weeks';
      return `${weeks} ${weekLabel}`;
    }
    return `${totalDurationDays} days`;
  }, [totalDurationDays]);

  const bundleEligible = selectedTracks.length >= 2;

  const rawTotalPrice = useMemo(() => (
    [...selectedTracks, ...selectedAddons]
      .map((pkg) => estimatePrice(pkg))
      .reduce((sum, value) => sum + value, 0)
  ), [selectedTracks, selectedAddons]);

  const compatibleAddonIds = useMemo(() => {
    if (selectedTracks.length === 0) return new Set<number>();
    const ids = addons
      .filter((addon) => addon.id != null && isAddonCompatible(addon, selectedTracks))
      .map((addon) => addon.id as number);
    return new Set(ids);
  }, [addons, selectedTracks, isAddonCompatible]);

  const discountAmount = useMemo(() => {
    if (!bundleEligible) return 0;
    const amount = selectedProgram?.bundle_discount_amount ?? pricing?.bundle_discount_amount ?? 0;
    return amount > 0 ? amount : 0;
  }, [selectedProgram, pricing?.bundle_discount_amount, bundleEligible]);

  const appliedPartnerDiscount = useMemo(() => {
    if (!partnerDiscount) return 0;
    if (partnerDiscountCurrency === '%') {
      return rawTotalPrice * (partnerDiscount / 100);
    }
    const programCurrency = (selectedProgram?.currency || pricing?.base_currency || '').toUpperCase();
    if (!programCurrency || !partnerDiscountCurrency) return partnerDiscount;
    return partnerDiscountCurrency.toUpperCase() === programCurrency ? partnerDiscount : 0;
  }, [partnerDiscount, partnerDiscountCurrency, pricing?.base_currency, rawTotalPrice, selectedProgram?.currency]);

  const totalPrice = Math.max(0, rawTotalPrice - discountAmount - appliedPartnerDiscount);

  useEffect(() => {
    if (selectedAddons.length === 0) return;
    const validIds = selectedAddons
      .filter((addon) => addon.id != null)
      .map((addon) => addon.id as number)
      .filter((id) => compatibleAddonIds.has(id));
    if (validIds.length === selectedAddons.length) return;
    if (isControlled) {
      onSelectionChange?.({
        programId: effectiveProgramId,
        trackIds: effectiveTrackIds,
        addonIds: validIds,
        totalPrice,
        totalDurationDays,
      });
    } else {
      setSelectedAddonIds(validIds);
    }
  }, [
    compatibleAddonIds,
    effectiveAddonIds,
    effectiveProgramId,
    effectiveTrackIds,
    isControlled,
    onSelectionChange,
    selectedAddons,
    totalDurationDays,
    totalPrice,
  ]);

  useEffect(() => {
    if (!onSelectionChange) return;
    if (!effectiveProgramId) return;
    onSelectionChange({
      programId: effectiveProgramId,
      trackIds: effectiveTrackIds,
      addonIds: effectiveAddonIds,
      totalPrice,
      totalDurationDays,
    });
  }, [onSelectionChange, effectiveProgramId, effectiveTrackIds, effectiveAddonIds, totalPrice, totalDurationDays]);

  if (!isAdmin && !allowPublic) {
    return <div className="text-center py-20 text-red-400">Admin access required.</div>;
  }

  const containerClass = embedded ? 'w-full space-y-8' : 'max-w-5xl mx-auto px-6 py-8 space-y-8';

  return (
    <div className={containerClass}>
      {!hideHeader && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            {!embedded && <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">Pathway Builder</p>}
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">{headline || 'Pathway Selection Builder'}</h1>
            {subhead && <p className="text-sm text-gray-500 dark:text-slate-300 mt-1">{subhead}</p>}
          </div>
          {!hideProgramSelect && (
            <div className="min-w-[240px]">
              <label className="block text-xs uppercase text-gray-500 dark:text-slate-400 mb-2">Program</label>
              <select
                value={selectedProgramId ?? ''}
                onChange={(event) => {
                  const nextId = Number(event.target.value);
                  if (isControlled) {
                    onSelectionChange?.({
                      programId: nextId,
                      trackIds: [],
                      addonIds: [],
                      totalPrice: 0,
                      totalDurationDays: 0,
                    });
                  } else {
                    setSelectedProgramId(nextId);
                    setSelectedTrackIds([]);
                    setSelectedAddonIds([]);
                  }
                }}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              >
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>{program.title}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{selectedProgram?.title || ''}</h2>
            {selectedProgram?.host_universities?.length ? (
              <p className="text-sm text-gray-500 dark:text-slate-300">{selectedProgram.host_universities.join(', ')}</p>
            ) : null}
          </div>
          <div className="text-xs text-gray-500 dark:text-slate-400 uppercase">Overview</div>
        </div>
        {selectedProgram?.description ? (
          <div className="mt-4 text-sm text-gray-700 dark:text-slate-200">
            {selectedProgram.description}
          </div>
        ) : null}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700 dark:text-slate-200">
          <div className="rounded-lg bg-gray-50 p-3 dark:bg-slate-800">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Location</p>
            {selectedProgram?.location ? <p className="font-medium">{selectedProgram.location}</p> : null}
          </div>
          <div className="rounded-lg bg-gray-50 p-3 dark:bg-slate-800">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Host</p>
            {selectedProgram?.host_universities?.length ? (
              <p className="font-medium">{selectedProgram.host_universities.join(', ')}</p>
            ) : null}
          </div>
          <div className="rounded-lg bg-gray-50 p-3 dark:bg-slate-800">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Duration</p>
            {selectedProgram?.duration ? (
              <p className="font-medium">{selectedProgram.duration}</p>
            ) : null}
          </div>
          <div className="rounded-lg bg-gray-50 p-3 dark:bg-slate-800">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Cohorts</p>
            {tracks.length > 0 ? (
              <p className="font-medium">{tracks.map((track) => track.name).join(' · ')}</p>
            ) : null}
          </div>
        </div>
        {!autoShowSelection && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => {
                setShowSelection(true);
                window.requestAnimationFrame(() => {
                  selectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
              }}
              className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Continue to Study Track Selection
            </button>
          </div>
        )}
      </div>

      {showSelection && (
        <div ref={selectionRef} className="grid grid-cols-1 lg:grid-cols-[1.6fr_0.9fr] gap-6">
          <div className="space-y-6">
            <div className="border-t border-gray-200 pt-6 dark:border-slate-700">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Choose Your Study Track</h3>
        <p className="text-sm text-gray-500 dark:text-slate-300">Select one or more core academic tracks.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {tracks.map((track) => (
            <label key={`track-${track.id ?? track.name}`} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:border-blue-400 cursor-pointer dark:border-slate-700 dark:bg-slate-900 dark:hover:border-blue-500">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{track.name || 'Core Track'}</div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">{formatDuration(track)}</div>
                </div>
                <input
                  type="checkbox"
                  checked={track.id != null ? effectiveTrackIds.includes(track.id) : false}
                  onChange={(event) => {
                    if (track.id == null) return;
                    const trackId = track.id;
                    if (isControlled) {
                      const nextTrackIds = event.target.checked
                        ? [...effectiveTrackIds, trackId]
                        : effectiveTrackIds.filter((id) => id !== trackId);
                      onSelectionChange?.({
                        programId: effectiveProgramId,
                        trackIds: nextTrackIds,
                        addonIds: effectiveAddonIds,
                        totalPrice,
                        totalDurationDays,
                      });
                    } else {
                      setSelectedTrackIds((prev) => (
                        event.target.checked ? [...prev, trackId] : prev.filter((id) => id !== trackId)
                      ));
                    }
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600"
                />
              </div>
              <div className="mt-3 text-xs text-gray-500 dark:text-slate-400">{track.description || 'Brief description of focus.'}</div>
              <div className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">Price: {formatCurrency(estimatePrice(track))}</div>
            </label>
          ))}
          {tracks.length === 0 && <div className="text-sm text-gray-500 dark:text-slate-400">No tracks yet.</div>}
        </div>
          {bundleEligible && (
            <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-700 dark:border-blue-900 dark:bg-blue-900/30 dark:text-blue-200">
              ✔ Combined Academic Track ({totalDurationSummary}) selected.
              {discountAmount > 0 && (
                <div className="mt-1 text-xs text-blue-700 dark:text-blue-200">
                  Bundle discount applied: -{formatCurrency(discountAmount)}
                  {(selectedProgram?.bundle_discount_description || pricing?.bundle_discount_description)
                    ? ` · ${selectedProgram?.bundle_discount_description || pricing?.bundle_discount_description}`
                    : ''}
                </div>
              )}
              {appliedPartnerDiscount > 0 && (
                <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-200">
                  Partner discount applied: -{formatCurrency(appliedPartnerDiscount)}
                  {partnerDiscountCurrency === '%' ? ` (${partnerDiscount}%)` : ''}
                </div>
              )}
            </div>
          )}
            </div>

            <div className="border-t border-gray-200 pt-6 dark:border-slate-700">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Optional Extensions</h3>
        <p className="text-sm text-gray-500 dark:text-slate-300">Enhance your experience (not required).</p>
        <div className="mt-4 space-y-3">
          {addons.map((addon) => (
            <label
              key={`addon-${addon.id ?? addon.name}`}
              className={`flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 ${
                addon.id != null && compatibleAddonIds.size > 0 && !compatibleAddonIds.has(addon.id)
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:border-emerald-400 cursor-pointer'
              } dark:border-slate-700 dark:bg-slate-900 dark:hover:border-emerald-500`}
            >
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{addon.name || 'Extension'}</div>
                <div className="text-xs text-gray-500 dark:text-slate-400">{formatDuration(addon)}</div>
                <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">{addon.description || 'Academic travel and guided site visits.'}</div>
                {addon.id != null && compatibleAddonIds.size > 0 && !compatibleAddonIds.has(addon.id) && (
                  <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-200">
                    Not compatible with your selected core track dates.
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">Add-on price: {formatCurrency(estimatePrice(addon))}</div>
                <input
                  type="checkbox"
                  checked={addon.id != null ? effectiveAddonIds.includes(addon.id) : false}
                  onChange={(event) => {
                    if (addon.id == null) return;
                    if (compatibleAddonIds.size > 0 && !compatibleAddonIds.has(addon.id)) return;
                    const addonId = addon.id;
                    if (isControlled) {
                      const nextAddonIds = event.target.checked
                        ? [...effectiveAddonIds, addonId]
                        : effectiveAddonIds.filter((id) => id !== addonId);
                      onSelectionChange?.({
                        programId: effectiveProgramId,
                        trackIds: effectiveTrackIds,
                        addonIds: nextAddonIds,
                        totalPrice,
                        totalDurationDays,
                      });
                    } else {
                      setSelectedAddonIds((prev) => (
                        event.target.checked ? [...prev, addonId] : prev.filter((id) => id !== addonId)
                      ));
                    }
                  }}
                  disabled={addon.id != null && compatibleAddonIds.size > 0 && !compatibleAddonIds.has(addon.id)}
                  className="mt-2 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 dark:border-slate-600"
                />
              </div>
            </label>
          ))}
          {addons.length === 0 && <div className="text-sm text-gray-500 dark:text-slate-400">No extensions yet.</div>}
        </div>
            </div>
          </div>
          <div className="lg:sticky lg:top-6 h-fit">
            <div className="border-t border-gray-200 pt-6 dark:border-slate-700">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Your Selection</h3>
        <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="space-y-2 text-sm text-gray-700 dark:text-slate-200">
            {selectedTracks.map((track) => (
              <div key={`summary-track-${track.id}`} className="flex items-center justify-between">
                <span>✔ {track.name} ({formatDuration(track)})</span>
                <span className="font-semibold">{formatCurrency(estimatePrice(track))}</span>
              </div>
            ))}
            {selectedAddons.map((addon) => (
              <div key={`summary-addon-${addon.id}`} className="flex items-center justify-between">
                <span>✔ {addon.name} ({formatDuration(addon)})</span>
                <span className="font-semibold">{formatCurrency(estimatePrice(addon))}</span>
              </div>
            ))}
            {discountAmount > 0 && (
              <div className="flex items-center justify-between text-emerald-700 dark:text-emerald-200">
                <span>Bundle discount</span>
                <span className="font-semibold">-{formatCurrency(discountAmount)}</span>
              </div>
            )}
            {(selectedTracks.length === 0 && selectedAddons.length === 0) && (
              <div className="text-sm text-gray-500 dark:text-slate-400">Select tracks and extensions to see totals.</div>
            )}
          </div>
          <div className="mt-4 border-t border-gray-200 pt-4 dark:border-slate-700">
            <div className="text-sm text-gray-500 dark:text-slate-400">Total Duration</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">{totalDurationSummary}</div>
            {discountAmount > 0 && (
              <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
                Discount applied: -{formatCurrency(discountAmount)}
                {(selectedProgram?.bundle_discount_description || pricing?.bundle_discount_description)
                  ? ` · ${selectedProgram?.bundle_discount_description || pricing?.bundle_discount_description}`
                  : ''}
              </div>
            )}
            {appliedPartnerDiscount > 0 && (
              <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:border-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">
                Partner discount applied: -{formatCurrency(appliedPartnerDiscount)}
                {partnerDiscountCurrency === '%' ? ` (${partnerDiscount}%)` : ''}
              </div>
            )}
            <div className="mt-3 text-sm text-gray-500 dark:text-slate-400">Estimated Total Cost</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(totalPrice)}</div>
            <button
              type="button"
              className="mt-4 w-full rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              Continue to Application
            </button>
          </div>
        </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PathwaySelectionBuilder;
